/**
 * Building entrances: where you actually get in.
 *
 * 1,699 mapped downtown, which is unusually good coverage — somebody walked
 * the blocks. This replaced the underground view, and the contrast is the
 * point: that layer inferred depth from a drawing-order tag and had 7 car
 * parks in the whole of downtown, while every node here is a surveyed point
 * sitting on the building outline where the door is.
 *
 * Drawn as small grey pictograms — a door, a gate, a shutter — sized in metres
 * so they sit in proportion to the building they belong to and fade out as you
 * zoom away. Colour is spent on live data, so the static fabric of the city
 * stays greyscale and never competes with it.
 *
 * Four kinds only. OSM's eight split the map into near-identical dots over
 * distinctions nobody asks a map for; these four differ in how you would use
 * them. Everything else OSM knows still shows on hover.
 */

import { IconLayer, SolidPolygonLayer } from "@deck.gl/layers";

import { Layer } from "./base.js";
import { palette, themeName } from "../theme/palette.js";
import { footprints } from "../map/footprints.js";
import { bearingOf, openingOf, slab } from "./solids.js";
import { DEPTH_TESTED, OVER_EVERYTHING } from "../map/depth.js";

/** Pictogram styling, resolved from the active palette. */
function styles() {
  const c = palette();
  return {
    door:    { grey: c.entranceDoor,    size: 3.2, shape: "door",   label: "Door" },
    gate:    { grey: c.entranceGate,    size: 4.4, shape: "gate",   label: "Gate" },
    service: { grey: c.entranceService, size: 4.4, shape: "garage", label: "Service / garage" },
    exit:    { grey: c.entranceExit,    size: 3.2, shape: "exit",   label: "Exit" },
  };
}

const styleOf = (kind) => styles()[kind] ?? styles().door;

const ICON_PX = 32;

/**
 * Doors are drawn at the height of a door handle rather than at zero.
 *
 * Flat on the ground they z-fight with it and tear as the camera moves, and a
 * door is a thing in a wall, not a mark on the pavement. Just over a metre
 * puts the pictogram where the door actually is.
 */
const DOOR_HEIGHT_M = 1.2;

/**
 * Grid cell for the entrance spatial index, in degrees (~55m).
 *
 * Wider than the "is a door already here" tolerance, so a query only has to
 * look at the nine cells around it and never miss a neighbour on a boundary.
 */
const GRID_CELL = 0.0005;

/** Below this zoom doors are noise; above it they are the point. */
export const DOOR_MIN_ZOOM = 15.5;

let icons = null;
let iconTheme = null;

/**
 * One icon per kind, drawn to look like the thing it marks.
 *
 * Plain squares were unreadable — four grey blocks differing only in shade. A
 * door gets a door: a panel with a handle. A gate gets bars between posts. A
 * garage gets a roller shutter. The silhouette carries the meaning, so it
 * survives at small sizes and in a screenshot, where a shade difference does
 * not.
 */
function iconSet() {
  // Cached as bitmaps, so the cache is keyed on the theme that drew them.
  const theme = themeName();
  if (icons && iconTheme === theme) return icons;
  iconTheme = theme;
  icons = {};

  for (const [kind, style] of Object.entries(styles())) {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = ICON_PX;
    const ctx = canvas.getContext("2d");
    const g = style.grey;
    const ink = `rgb(${g},${g},${g})`;

    // Dark plate behind everything, so the icon holds up over a pale rooftop.
    ctx.fillStyle = palette().entrancePlate;
    ctx.fillRect(3, 3, ICON_PX - 6, ICON_PX - 6);

    const x0 = 8;
    const y0 = 6;
    const w = ICON_PX - 16;
    const h = ICON_PX - 12;

    if (style.shape === "gate") {
      ctx.strokeStyle = ink;
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.moveTo(x0, y0); ctx.lineTo(x0, y0 + h);
      ctx.moveTo(x0 + w, y0); ctx.lineTo(x0 + w, y0 + h);
      ctx.stroke();

      ctx.lineWidth = 1.8;
      for (let i = 1; i < 4; i += 1) {
        const x = x0 + (w * i) / 4;
        ctx.beginPath();
        ctx.moveTo(x, y0 + 3); ctx.lineTo(x, y0 + h - 3);
        ctx.stroke();
      }

      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.moveTo(x0, y0 + 4); ctx.lineTo(x0 + w, y0 + 4);
      ctx.moveTo(x0, y0 + h - 4); ctx.lineTo(x0 + w, y0 + h - 4);
      ctx.stroke();
    } else if (style.shape === "garage") {
      ctx.strokeStyle = ink;
      ctx.lineWidth = 2.4;
      ctx.strokeRect(x0, y0, w, h);
      ctx.lineWidth = 1.6;
      for (let i = 1; i < 5; i += 1) {
        const y = y0 + (h * i) / 5;
        ctx.beginPath();
        ctx.moveTo(x0 + 2, y); ctx.lineTo(x0 + w - 2, y);
        ctx.stroke();
      }
    } else {
      // Door: a filled panel with a handle. An exit is the same object with
      // the leading edge ajar — it is a door you leave by, not a new shape.
      ctx.fillStyle = ink;
      ctx.fillRect(x0, y0, w, h);
      ctx.fillStyle = "rgba(0,0,0,0.72)";
      ctx.beginPath();
      ctx.arc(x0 + w - 4.5, y0 + h / 2, 1.9, 0, Math.PI * 2);
      ctx.fill();
      if (style.shape === "exit") ctx.fillRect(x0 + 2, y0 + 2, 1.8, h - 4);
    }

    icons[kind] = {
      url: canvas.toDataURL(),
      width: ICON_PX, height: ICON_PX,
      anchorX: ICON_PX / 2, anchorY: ICON_PX / 2,
      mask: false,
    };
  }
  return icons;
}

/**
 * Extrudable slabs for a set of entrances.
 *
 * The wall a door belongs to comes from the shared footprint index. When there
 * is no building loaded under the point — off-screen, or a gate standing in the
 * open — the slab falls back to unrotated, which for a gate is honest enough
 * and for a door is the best available.
 */
/**
 * How far a door may be pulled onto the wall it belongs to, in metres.
 *
 * The OSM node and the surveyed outline routinely disagree by a few metres;
 * left where the node says, a depth-tested slab that far inside the extrusion
 * is a door nobody can see, and that far outside it is a door floating in the
 * street. Beyond this the nearest wall is probably not this door's wall at
 * all — a gate standing in the open — and the node is left alone.
 */
const SNAP_M = 8;

function openings(entrances) {
  const rows = [];
  for (const entrance of entrances) {
    const size = openingOf(entrance.kind);
    const edge = footprints.edgeNear(entrance.lng, entrance.lat);
    const bearing = edge ? bearingOf(edge.a, edge.b) : 0;
    // On the wall, not merely near it: the slab is centred on the closest
    // point of the nearest edge whenever that edge is plausibly its wall.
    const [lng, lat] = edge && edge.meters <= SNAP_M
      ? edge.point : [entrance.lng, entrance.lat];
    rows.push({
      at: [lng, lat],
      polygon: slab({
        lng,
        lat,
        bearing,
        width: size.width,
        depth: size.depth,
      }),
      height: size.height,
      color: (() => { const g = styleOf(entrance.kind).grey; return [g, g, g, 235]; })(),
      source: entrance,
    });
  }
  return rows;
}

export class EntranceLayer extends Layer {
  static key = "entrances";
  static label = "Entrances";
  static description = "Building doors from OSM, main entrances highlighted";

  async load() {
    this.status = "loading";
    try {
      // Baked citywide at build time (scripts/bake.mjs), not the downtown-only
      // /api/entrances: ~30k doors across the metro, drawn only where you are
      // looking (see renderInView).
      const response = await fetch("./data/entrances.json");
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.json();
      this._entrances = body.entrances ?? [];
      this.status = this._entrances.length ? "ready" : "unavailable";
      this.detail = `${this._entrances.length} mapped`;
      return null;
    } catch (error) {
      this.status = "unavailable";
      this.detail = `Unavailable (${error.message})`;
      return null;
    }
  }

  /**
   * The entrances inside a [[w,s],[e,n]] box, capped.
   *
   * 30k doors citywide is more geometry than a frame can build, and the ones
   * off screen are not being looked at. The cap is a backstop: at the zoom
   * these are drawn the viewport holds tens, not thousands, so it is rarely
   * reached — but a pathological dense block must not stall the pan.
   */
  inBounds(bounds, limit = 1500) {
    if (!bounds) return [];
    const [[west, south], [east, north]] = bounds;
    const out = [];
    for (const e of this._entrances ?? []) {
      // The legend filter is applied HERE rather than to the result, because
      // the cap below is a cap on what gets drawn. Filtering afterwards would
      // take 1,500 mostly-doors and then hide the doors, leaving a handful of
      // gates on a screen that holds hundreds — a filter that also silently
      // throws away most of what you asked to keep.
      if (this.isHidden(styles()[e.kind] ? e.kind : "door")) continue;
      if (e.lng >= west && e.lng <= east && e.lat >= south && e.lat <= north) {
        out.push(e);
        if (out.length >= limit) break;
      }
    }
    return out;
  }

  /**
   * Is there a surveyed entrance within ~20m of this point?
   *
   * The inferred-door pass asks this for every footprint in view so it never
   * draws a guess on top of a real door. A linear scan over 30k entrances,
   * once per footprint, is tens of millions of comparisons a pan — so the
   * points go into a coarse grid built once and only the nine cells around the
   * query are checked.
   */
  hasNear(lng, lat, dLng = 0.00025, dLat = 0.0002) {
    if (!this._grid) {
      this._grid = new Map();
      for (const e of this._entrances ?? []) {
        const key = `${Math.round(e.lng / GRID_CELL)}:${Math.round(e.lat / GRID_CELL)}`;
        const cell = this._grid.get(key);
        if (cell) cell.push(e); else this._grid.set(key, [e]);
      }
    }
    const cx = Math.round(lng / GRID_CELL);
    const cy = Math.round(lat / GRID_CELL);
    for (let ix = cx - 1; ix <= cx + 1; ix += 1) {
      for (let iy = cy - 1; iy <= cy + 1; iy += 1) {
        const cell = this._grid.get(`${ix}:${iy}`);
        if (!cell) continue;
        for (const e of cell) {
          if (Math.abs(e.lng - lng) < dLng && Math.abs(e.lat - lat) < dLat) return true;
        }
      }
    }
    return false;
  }

  get loadedCount() {
    return this._entrances?.length ?? 0;
  }

  legend() {
    return Object.entries(styles()).map(([kind, style]) => ({
      key: kind,
      rgb: [style.grey, style.grey, style.grey],
      label: style.label,
    }));
  }

  describe(picked) {
    // Both sublayers land here: the slab rows carry the entrance as `source`,
    // the raw rows are the entrance itself.
    const entrance = picked?.source ?? picked;
    const style = styleOf(entrance.kind);
    const rows = [["Type", style.label]];
    // The map draws four kinds; OSM's own value is still worth being able to
    // ask for, so it stays here.
    if (entrance.detail && entrance.detail !== entrance.kind) {
      rows.push(["Tagged", String(entrance.detail).replace(/_/g, " ")]);
    }

    if (entrance.wheelchair) {
      // Spelled out rather than passed through: "limited" on its own does not
      // tell you what is limited about it.
      const access = {
        yes: "Step-free",
        limited: "Step-free with difficulty",
        no: "Not step-free",
      }[entrance.wheelchair] ?? entrance.wheelchair;
      rows.push(["Wheelchair", access]);
    }
    if (entrance.door) rows.push(["Door", String(entrance.door).replace(/_/g, " ")]);
    if (entrance.access) rows.push(["Access", String(entrance.access).replace(/_/g, " ")]);
    if (entrance.level) rows.push(["Level", entrance.level]);
    rows.push(["Source", "OpenStreetMap"]);

    return { title: entrance.name || style.label, rows };
  }

  // Nothing from the shared per-layer render pass: 30k openings rebuilt on
  // every full paint would be a spike, and they mean nothing zoomed out. The
  // map composes these through renderInView on the per-pan path instead.
  render() {
    return [];
  }

  /**
   * The openings and pictograms for the current viewport.
   *
   * Called from the same per-pan, zoom-gated path as the inferred doors, so
   * only what is on screen is built and only when the doors are worth drawing.
   * Below DOOR_MIN_ZOOM there are more doors on screen than could be told apart
   * anyway, so nothing is drawn.
   */
  renderInView(zoom, bounds) {
    if (!this.enabled || this.status !== "ready"
        || !bounds || zoom < DOOR_MIN_ZOOM) {
      this.visibleCount = 0;
      return [];
    }
    const data = this.inBounds(bounds);
    this.visibleCount = data.length;
    if (!data.length) return [];

    const icon = iconSet();
    const rows = openings(data);

    return [
      // The opening itself, as geometry.
      //
      // A door used to be a billboard sprite: a pale rectangle facing the
      // camera at door-handle height, and a garage was the same rectangle
      // slightly larger. Neither looked like the thing it marked, and at a tilt
      // they both lay flat against the wall. A door is a metre wide, 35cm deep
      // and 2.1m tall, and a garage is three times as wide and half a metre
      // taller — extrude those and the shapes tell you which is which with no
      // artwork at all.
      //
      // Orientation comes from the building's nearest wall, because an OSM
      // entrance is a bare point with no direction on it. Without that the slab
      // sits across the doorway instead of in it.
      new SolidPolygonLayer({
        id: `${this.key}-openings`,
        data: rows,
        extruded: true,
        filled: true,
        getPolygon: (d) => d.polygon,
        getElevation: (d) => d.height,
        getFillColor: (d) => d.color,
        pickable: true,
        // Depth tested, unlike the sprite it replaces: it is real geometry
        // standing in the wall now, so being occluded by the building it
        // belongs to is correct rather than something to work around.
        parameters: DEPTH_TESTED,
      }),
      // The pictogram stays, small, on top of the opening — the extrusion says
      // "a door is here", the icon still says which sort.
      new IconLayer({
        id: `${this.key}-points`,
        // The opening rows, not the raw entrances: the slab may have been
        // snapped onto its wall, and the pictogram belongs on the slab.
        data: rows,
        getIcon: (d) => icon[d.source.kind] ?? icon.door,
        getPosition: (d) => [d.at[0], d.at[1], openingOf(d.source.kind).height + 0.4],
        getSize: (d) => styleOf(d.source.kind).size * 0.6,
        // Metres, not pixels: a door is a fixed thing on a building, so it
        // should grow with the building as you zoom rather than float at a
        // constant screen size. No minimum — zoomed out they shrink away to
        // nothing, which is correct. You zoom in to see doors.
        sizeUnits: "meters",
        sizeMinPixels: 0,
        sizeMaxPixels: 64,
        billboard: true,
        pickable: true,
        // Doors sit against the wall of the extrusion they belong to and
        // would otherwise be swallowed by it.
        parameters: OVER_EVERYTHING,
      }),
    ];
  }
}

/* ── Inferred doors ─────────────────────────────────────────────────────────
 *
 * OSM maps 2,088 entrances against 43,071 buildings, so 95% of the city has no
 * door on it. These fill that in from the geometry: every building gets a door
 * cut into its longest wall, drawn as a segment of the outline itself rather
 * than as a pin floating near it.
 *
 * This is inference and is labelled as such everywhere it surfaces. The
 * longest edge is a reasonable proxy for the street frontage and is wrong
 * often enough that it must never be confused with the surveyed nodes — those
 * stay a distinct, brighter symbol. What this is good for is reading the
 * *fabric*: which walls face the street, where a block is enterable.
 */

/** Door width in metres, drawn along the wall. */
const DOOR_W = 1.6;

const metres = (ax, ay, bx, by) =>
  Math.hypot((ax - bx) * 85600, (ay - by) * 110540);

/**
 * A door segment on each building's longest wall.
 *
 * Restricted to the viewport and capped: 43,071 buildings is far more geometry
 * than a frame can absorb, and the ones off screen are not being looked at.
 */
/**
 * The footprints this would walk, as an array that can be handed out in pieces.
 *
 * inferredDoors is one pass over every footprint in view, and at a hundred
 * thousand of them that is a third of a second in a single call — which is what
 * was left of the pan hitch after the viewsheds were sliced. Exposing the input
 * lets a caller feed it a chunk at a time and stay inside a frame budget.
 */
export function doorCandidates(index) {
  return index ? [...index.all()] : [];
}

export function inferredDoors(index, bounds, mapped, { limit = 4000, rows } = {}) {
  if (!bounds || !index) return [];
  const [[west, south], [east, north]] = bounds;
  const out = [];

  for (const row of rows ?? index.all()) {
    if (out.length >= limit) break;
    const { ring } = row;
    if (!Array.isArray(ring) || ring.length < 4) continue;

    // Longest edge, which is usually the frontage on a rectangular footprint.
    let best = null;
    let bestLen = 0;
    let inView = false;
    for (let i = 0; i < ring.length - 1; i += 1) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[i + 1];
      if (x1 >= west && x1 <= east && y1 >= south && y1 <= north) inView = true;
      const len = metres(x1, y1, x2, y2);
      if (len > bestLen) { bestLen = len; best = [x1, y1, x2, y2]; }
    }
    // A wall shorter than a door is not a frontage; skip rather than invent.
    if (!inView || !best || bestLen < 3) continue;

    const [x1, y1, x2, y2] = best;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;

    // Already surveyed here? Then the real node speaks for this building.
    if (mapped?.(mx, my)) continue;

    // A DOOR_W slice of the wall, centred on its midpoint.
    const half = DOOR_W / 2 / bestLen;
    const dx = (x2 - x1) * half;
    const dy = (y2 - y1) * half;
    out.push({
      path: [
        [mx - dx, my - dy, DOOR_HEIGHT_M],
        [mx + dx, my + dy, DOOR_HEIGHT_M],
      ],
      height: row.height,
      inferred: true,
    });
  }
  return out;
}

export function inferredDoorLayer(doors, { visible = true } = {}) {
  // Doors that stand up, like the mapped ones.
  //
  // These were left as a PathLayer when the mapped entrances became extruded
  // slabs, and they are 95% of the doors on the map — OSM has an entrance for
  // about one building in twenty — so almost every door anybody actually sees
  // went back to being a line lying against a wall.
  //
  // The two ends of the path give the wall's direction for free: they were
  // computed by walking the footprint's longest edge, so the bearing between
  // them is the frontage, which is exactly what a slab needs to sit in.
  const size = openingOf("door");
  const rows = [];

  for (const door of doors) {
    const path = door?.path;
    if (!Array.isArray(path) || path.length < 2) continue;
    const [a, b] = path;
    rows.push({
      polygon: slab({
        lng: (a[0] + b[0]) / 2,
        lat: (a[1] + b[1]) / 2,
        bearing: bearingOf(a, b),
        width: size.width,
        depth: size.depth,
      }),
      source: door,
    });
  }

  return new SolidPolygonLayer({
    id: "entrances-inferred",
    data: rows,
    visible,
    extruded: true,
    filled: true,
    getPolygon: (d) => d.polygon,
    getElevation: size.height,
    getFillColor: [...palette().entranceInferred, 225],
    pickable: true,
    // Real geometry standing in a wall, so being occluded by the building it
    // belongs to is correct rather than something to work around.
    parameters: DEPTH_TESTED,
  });
}

export function describeInferredDoor() {
  return {
    title: "Door (inferred)",
    rows: [
      ["Type", "Door"],
      ["Position", "Estimated from the footprint's longest wall"],
      ["Surveyed", "No — OSM has no entrance here"],
      ["Source", "Derived from Denver building outlines"],
    ],
  };
}
